import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { withErrorHandling } from "../api/axiosHelper";
import { ApiError, ApiResponse } from "../api/types";

type Result<TData> = [
  TData | undefined,
  ApiError | undefined,
  {
    setData: Dispatch<SetStateAction<TData | undefined>>;
    isFetching: boolean;
    fetch: (cancellationToken?: CancellationToken) => Promise<void>;
    cancel: () => void;
  },
];

// Needed for useEffect called twice in development mode
interface CancellationToken {
  cancel: boolean;
}

const useFetch = <TData>(
  apiCall: (signal?: AbortSignal) => Promise<ApiResponse<TData>>,
  onSetData?: (data: TData) => void
): Result<TData> => {
  const [data, setData] = useState<TData>();
  const [error, setError] = useState<ApiError>();
  const [isFetching, setIsFetching] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  const handledApiCall = useMemo(() => withErrorHandling(apiCall), [apiCall]);

  const fetch = useCallback(
    async (cancellationToken?: CancellationToken) => {
      abortControllerRef.current?.abort();
      abortControllerRef.current = new AbortController();

      setIsFetching(true);
      setError(undefined);

      const [responseData, responseError] = await handledApiCall(abortControllerRef.current?.signal);

      if (cancellationToken?.cancel || abortControllerRef.current?.signal.aborted) {
        return;
      }

      if (responseError) {
        setError(responseError.isCanceledRequest ? undefined : responseError);
      } else {
        setData(responseData);
        onSetData?.(responseData);
      }

      setIsFetching(false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [handledApiCall]
  );

  useEffect(() => {
    const cancellationToken: CancellationToken = { cancel: false };
    fetch(cancellationToken);

    return () => {
      cancellationToken.cancel = true;
      abortControllerRef.current?.abort();
    };
  }, [fetch]);

  const cancel = useCallback(() => {
    abortControllerRef.current?.abort();
  }, []);

  return [data, error, { setData, isFetching, fetch, cancel }];
};

export default useFetch;
